import queryString from 'query-string';
import { FC, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { useHistory, useLocation } from 'react-router';

import { CycleI, PackName } from '~src/interfaces/cycle.interface';
import { CardsContainer } from '~src/modules/questions/components/shared.sc';
import { StoreI } from '~src/rootReducer';
import { routes } from '~src/routingConfig';
import { ApiReduxResp, isSuccess } from '~src/services/apiMiddleware';
import logger from '~src/services/logger/LlsLogger';
import { fetchCycles } from '~src/shared/sidebar/actions/sidebarActions';
import { isAdmin } from '~src/store/session/session.reducer';

import AssessmentStream from './AssessmentStream';
import ManagerSelection from './ManagerSelection';
import SelfSelection from './SelfSelection';
import CustomSelection from './CustomSelection';

const FINISH_TIMEOUT = 800;

interface OwnProps {
  cycleId: string;
}

interface StateProps {
  cycles: Record<string, CycleI>;
  isAdmin: boolean;
  simpleGui: boolean;
}

interface DispatchProps {
  fetchCycles: () => ApiReduxResp<CycleI[]>;
}

type Props = OwnProps & StateProps & DispatchProps;

const Questions: FC<Props> = (props: Props) => {
  const history = useHistory();
  const location = useLocation();

  const query: { selected_pack?: PackName; precedent?: string } = queryString.parse(location.search);
  const [loadedPackName, setLoadedPackName] = useState(query.selected_pack || PackName.Assessment);

  const currentCycle: CycleI | undefined = useMemo(() => {
    return props.cycles[props.cycleId];
  }, [props.cycleId, props.cycles]);

  useEffect(() => {
    console.info('Questions#useEffect cycle', currentCycle?.id);
    if (!currentCycle) {
      history.replace(routes.answerQuestions.questions);
      return;
    }

    const selectedPackName = queryString.parse(location.search).selected_pack;
    let selectedPack = selectedPackName as PackName;
    if (selectedPack === undefined) {
      const defaultPack = currentCycle.packs.find((el) => !el.finished);
      const packName = defaultPack ? defaultPack.name : PackName.Assessment;
      selectedPack = packName;
      console.info('no selected pack, oh noez - navigating to URL with selected pack');
      history.replace({
        search: `?selected_pack=${packName}${query.precedent ? `&precedent=${query.precedent}` : ''}`,
      });
    }
    setLoadedPackName(selectedPack);
  }, [currentCycle]);

  const finishQS = (pack: PackName) => {
    setTimeout(() => {
      // const cycleIndex = saveCurrentCycle(props.cycleId, pack);
      const oldCyclesLength = props.cycles ? Object.keys(props.cycles).length : 0;
      void props.fetchCycles().then((res) => {
        if (isSuccess(res)) {
          const cycles = res.response.data;

          if (cycles && cycles.length === 1 && oldCyclesLength === 1) {
            history.push('/questions/no-survey');
            return;
          }
          history.push(`/questions/cycle/finished?lastCycle=${props.cycleId}&packName=${pack}`);
        } else {
          logger.error(res.error.message);
        }
      });
    }, FINISH_TIMEOUT);
  };

  const renderStream = (streamProps: {
    precedent: string | undefined;
    finishQS: (pack: PackName) => void;
    cycle: CycleI;
  }) => {
    console.info('Questions rendering with loadedPackName', loadedPackName);
    switch (loadedPackName) {
      case PackName.SelfSelection:
        return <SelfSelection {...streamProps} />;
      case PackName.ManagersSelection:
        return <ManagerSelection {...streamProps} />;
      case PackName.SelfAssessment:
        return <AssessmentStream {...streamProps} self />;
      case PackName.Assessment:
        return <AssessmentStream {...streamProps} />;
      case PackName.CustomSelector:
        return <CustomSelection {...streamProps} />;
    }
  };

  if (!currentCycle) {
    logger.warn('<Questions> is to be rendered, but state does not contain cycle ' + props.cycleId);
    return null;
  }

  const streamProps = {
    finishQS: finishQS,
    cycle: currentCycle,
    precedent: query.precedent,
  };

  return <CardsContainer>{renderStream(streamProps)}</CardsContainer>;
};

const mapStateToProps = (state: StoreI): StateProps => ({
  cycles: state.user.cycles.byId || {},
  isAdmin: isAdmin(state),
  simpleGui: state.user.session.company ? state.user.session.company.simple_gui : false,
});

const mapDispatchToProps = (dispatch: any): DispatchProps => ({
  fetchCycles: () => dispatch(fetchCycles()),
});

const QuestionsSuperComponent = connect(mapStateToProps, mapDispatchToProps)(Questions);

export default QuestionsSuperComponent;
